import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Edge functions are deployed on the primary Supabase project (qqctfewfbrvypcaosbht),
// which is separate from the Lovable-managed project that VITE_SUPABASE_URL points to.
const X_FUNCTIONS_BASE = 'https://qqctfewfbrvypcaosbht.supabase.co';

interface XConnectionStatus {
  connected: boolean;
  username?: string;
  x_user_id?: string;
  token_expires_at?: string;
  last_sync?: {
    sync_type: string;
    tweets_fetched: number;
    tweets_inserted: number;
    started_at: string;
    completed_at: string | null;
    error: string | null;
  } | null;
}

export function useXBookmarks() {
  const { user } = useAuth();
  const [status, setStatus] = useState<XConnectionStatus | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    try {
      const res = await fetch(
        `${X_FUNCTIONS_BASE}/functions/v1/x-auth?action=status&user_id=${user.id}`,
        {
          headers: {
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
        }
      );

      if (res.ok) {
        const data = await res.json();
        setStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch X status:', err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const connect = useCallback(() => {
    if (!user) return;
    // Redirect to OAuth flow, pass app_redirect so callback returns to our app
    const appRedirect = encodeURIComponent(window.location.origin);
    const authUrl = `${X_FUNCTIONS_BASE}/functions/v1/x-auth?action=authorize&user_id=${user.id}&app_redirect=${appRedirect}`;
    window.location.href = authUrl;
  }, [user]);

  const disconnect = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(
        `${X_FUNCTIONS_BASE}/functions/v1/x-auth?action=disconnect`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
          },
          body: JSON.stringify({ user_id: user.id }),
        }
      );

      if (res.ok) {
        setStatus({ connected: false });
        toast.success('X account disconnected');
      }
    } catch (err) {
      toast.error('Failed to disconnect X account');
    }
  }, [user]);

  const syncNow = useCallback(
    async (mode: 'test' | 'backfill' | 'daily' = 'backfill', backfillMonths: number = 6) => {
      if (!user) return;
      setIsSyncing(true);
      try {
        // Get the user's current auth token to pass to the edge function
        const { data: sessionData } = await supabase.auth.getSession();
        const authToken = sessionData?.session?.access_token;

        if (!authToken) {
          toast.error('Not authenticated. Please sign in again.');
          setIsSyncing(false);
          return;
        }

        const res = await fetch(
          `${X_FUNCTIONS_BASE}/functions/v1/x-bookmarks-sync`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
            },
            body: JSON.stringify({
              mode,
              user_id: user.id,
              backfill_months: backfillMonths,
              // Pass app DB credentials so the edge function can write items
              app_url: import.meta.env.VITE_SUPABASE_URL,
              app_anon_key: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
              auth_token: authToken,
            }),
          }
        );

        if (res.ok) {
          const data = await res.json();
          toast.success(
            `Synced ${data.tweets_inserted} new tweet${data.tweets_inserted !== 1 ? 's' : ''}`
          );
          await fetchStatus();
        } else {
          const err = await res.json();
          toast.error(err.error || 'Sync failed');
        }
      } catch (err) {
        toast.error('Sync failed: ' + (err as Error).message);
      } finally {
        setIsSyncing(false);
      }
    },
    [user, fetchStatus]
  );

  return {
    status,
    isLoading,
    isSyncing,
    fetchStatus,
    connect,
    disconnect,
    syncNow,
  };
}
